import type { RequestInfo } from '@warp-drive/core/types/request';
import { SkipCache } from '@warp-drive/core-types/request';
import { SkipErrorReporting } from '../request-options.ts';
import { buildQueryRequestUrl } from '../utils/request-url.ts';

/**
 * GET /api/v2/employee-plan-accounts/:employeePlanAccountId/eligibility-criteria
 *
 * @param employeePlanAccountId - the caller's own account (participant-authorized)
 * @returns the plan's configured LoanStatus/LoanType values behind this account's current
 *   ineligibility — empty when its `good_standing_reason` has fixed alert copy instead
 */
export function getEmployeePlanAccountEligibilityCriteriaRequest(
  employeePlanAccountId: string
): RequestInfo {
  return {
    url: buildQueryRequestUrl(
      'employee-plan-account-eligibility-criteria',
      `api/v2/employee-plan-accounts/${encodeURIComponent(employeePlanAccountId)}/eligibility-criteria`
    ),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
    options: { [SkipErrorReporting]: true },
  };
}

/** A configured condition's raw values, keyed to the flavor of enum they're drawn from. */
export interface EmployeePlanAccountEligibilityCriteria {
  values: string[];
  flavor: 'LoanStatus' | 'LoanType' | null;
}

/** JSON:API document wrapping one eligibility-criteria resource. Attribute keys arrive dasherized. */
export interface EmployeePlanAccountEligibilityCriteriaResponse {
  data: {
    type: 'employee-plan-account-eligibility-criteria';
    id: string;
    attributes: Record<string, unknown>;
  };
}

/**
 * Maps the kit's JSON:API document onto {@link EmployeePlanAccountEligibilityCriteria}.
 *
 * @param response - the document returned by {@link getEmployeePlanAccountEligibilityCriteriaRequest}
 */
export function unwrapEmployeePlanAccountEligibilityCriteria(
  response: EmployeePlanAccountEligibilityCriteriaResponse
): EmployeePlanAccountEligibilityCriteria {
  const a = response?.data?.attributes ?? {};
  const values = a['values'];
  const flavor = a['flavor'];
  return {
    values: Array.isArray(values) ? values.map(String) : [],
    flavor: flavor === 'LoanStatus' || flavor === 'LoanType' ? flavor : null,
  };
}
